import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageArea from '../shared/PageArea';
import PageTitle from '../shared/PageTitle';
import PagedItems from '../shared/PagedItems';
import PipelineStepper, { pipelineState } from '../shared/PipelineStepper';
import { authenticatedFetch, getApiBaseUrl } from '../../api/apiConfig';
import {
  DocumentsClient,
  ProjectsClient,
  ProjectListItem,
  UploadBatchFileStatus,
  UploadBatchStatusResponse,
} from '../../web-api-client';

const UploadPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const projectsClient = useMemo(() => new ProjectsClient(getApiBaseUrl(), authenticatedFetch), []);
  const documentsClient = useMemo(() => new DocumentsClient(getApiBaseUrl(), authenticatedFetch), []);

  const [files, setFiles] = useState<File[]>([]);
  const [targetProjectId, setTargetProjectId] = useState<string>(searchParams.get('project') ?? '');
  const [clientReference, setClientReference] = useState('');
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [batches, setBatches] = useState<RecentUploadBatch[]>([]);
  const [batchPage, setBatchPage] = useState(1);
  const [batchTotal, setBatchTotal] = useState(0);
  const [pollError, setPollError] = useState(false);
  const pollRequestIdRef = useRef(0);
  const pollAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    projectsClient
      .getApiProjects(1, 200, undefined, false)
      .then((r) => setProjects(r.items ?? []))
      .catch(() => setProjects([]));
  }, [projectsClient]);

  useEffect(() => {
    let active = true;
    let timer: number | undefined;

    const poll = async () => {
      const requestId = ++pollRequestIdRef.current;
      pollAbortRef.current?.abort();
      const controller = new AbortController();
      pollAbortRef.current = controller;

      try {
        const response = await authenticatedFetch.fetch(
          `${getApiBaseUrl() ?? ''}/api/uploads/batches?pageNumber=${batchPage}&pageSize=5`,
          { signal: controller.signal },
        );
        if (!response.ok) {
          throw new Error(`Upload status request failed (${response.status})`);
        }
        const status = (await response.json()) as RecentUploadBatchesResult;
        if (active && requestId === pollRequestIdRef.current) {
          setBatches(status.items ?? []);
          setBatchTotal(status.totalCount ?? 0);
          setPollError(false);
        }
      } catch {
        if (!controller.signal.aborted && active && requestId === pollRequestIdRef.current) {
          setPollError(true);
        }
      } finally {
        if (active) {
          timer = window.setTimeout(poll, 4000);
        }
      }
    };

    void poll();
    return () => {
      active = false;
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
      pollAbortRef.current?.abort();
    };
  }, [batchPage]);

  const photosOnly = files.length > 0 && files.every((f) => f.type.startsWith('image/'));

  const onFilesSelected = (selected: FileList | null) => {
    if (!selected) {
      return;
    }

    setFiles((prev) => [...prev, ...Array.from(selected)]);
    setError(null);
  };

  const handleSubmit = async () => {
    setError(null);

    if (files.length === 0) {
      setError(t('upload.selectedFiles', { count: 0 }));
      return;
    }

    if (photosOnly && !targetProjectId) {
      setError(t('upload.photosNeedProject'));
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append('files', f, f.name));
      if (targetProjectId) {
        formData.append('targetProjectId', targetProjectId);
      }
      if (clientReference) {
        formData.append('clientReference', clientReference);
      }

      const response = await authenticatedFetch.fetch(`${getApiBaseUrl() ?? ''}/api/documents/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const problem = await response.json().catch(() => null);
        throw new Error(problem?.detail ?? problem?.title ?? t('upload.errors.failed', { status: response.status }));
      }

      const result = (await response.json()) as { batchId: string };
      const status = await documentsClient.getApiUploadsBatches(result.batchId);
      setBatchPage(1);
      setBatchTotal((current) => current + 1);
      setBatches((current) => [status as RecentUploadBatch, ...current.filter((batch) => batch.batchId !== status.batchId)].slice(0, 5));
      setFiles([]);
      setClientReference('');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageArea>
      <PageTitle title={t('upload.title')} subtitle={t('upload.subtitle')} />

      <Box
        sx={{
          display: 'grid',
          gap: 8,
          gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
          alignItems: 'start',
        }}
      >
        <Stack spacing={6} sx={{ minWidth: 0 }}>
          <Paper
            variant="outlined"
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              onFilesSelected(e.dataTransfer.files);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            sx={{
              p: 10,
              textAlign: 'center',
              borderStyle: 'dashed',
              borderWidth: 2,
              cursor: 'pointer',
              borderColor: dragging ? 'primary.main' : 'divider',
              bgcolor: dragging ? 'primary.25' : 'background.paper',
            }}
            component="label"
          >
            <input hidden type="file" multiple onChange={(e) => onFilesSelected(e.target.files)} />
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: 'primary.50',
                color: 'primary.main',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1,
              }}
            >
              <CloudUploadIcon />
            </Box>
            <Typography sx={{ mt: 1, fontWeight: 600 }}>{t('upload.dropzone')}</Typography>
            <Typography variant="textSM" color="text.secondary">
              {t('upload.dropzoneHint')}
            </Typography>
          </Paper>

          {files.length > 0 && (
            <Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {files.map((f, i) => (
                  <Chip key={`${f.name}-${i}`} label={f.name} onDelete={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))} />
                ))}
              </Stack>
              <Button size="md" sx={{ mt: 1 }} onClick={() => setFiles([])}>
                {t('upload.clear')}
              </Button>
            </Box>
          )}

          <FormControl fullWidth>
            <InputLabel>{t('upload.targetProject')}</InputLabel>
            <Select
              value={targetProjectId}
              label={t('upload.targetProject')}
              onChange={(e) => setTargetProjectId(e.target.value)}
            >
              <MenuItem value="">
                <em>{t('upload.unassociated')}</em>
              </MenuItem>
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {[p.projectNumber ?? p.internalReference, p.vesselName, p.cargoDescription].filter(Boolean).join(' · ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="textSM" color="text.secondary">
            {t('upload.targetProjectHint')}
          </Typography>

          <TextField
            label={t('upload.clientReference')}
            value={clientReference}
            onChange={(e) => setClientReference(e.target.value)}
            fullWidth
          />

          {error && <Alert severity="error">{error}</Alert>}

          <Button variant="primary" size="md" disabled={submitting || files.length === 0} onClick={handleSubmit}>
            {submitting ? t('common.uploading') : t('upload.uploadButton')}
          </Button>
        </Stack>

        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 8, borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 600, mb: 6 }}>{t('upload.howTitle')}</Typography>
            <HowStep title={t('upload.howClassify')} text={t('upload.howClassifyBody')} />
            <HowStep title={t('upload.howMatch')} text={t('upload.howMatchBody')} />
            <HowStep title={t('upload.howReview')} text={t('upload.howReviewBody')} />
          </Paper>
        </Stack>
      </Box>
      <Box sx={{ mt: 8, width: '100%' }}>
          {batches.length > 0 && (
            <Stack spacing={5}>
              {pollError && <Alert severity="error">{t('upload.errors.statusRefresh')}</Alert>}
              <Box>
                <Typography sx={{ fontWeight: 600 }}>{t('upload.recentBatches')}</Typography>
                <Typography sx={{ mt: 2, fontSize: 13, color: 'text.secondary' }}>{t('upload.recentBatchesHint')}</Typography>
              </Box>
              {batches.map((batch) => (
                <UploadBatchPanel key={batch.batchId} batch={batch} projects={projects} />
              ))}
              {batchTotal > 5 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
                  <Pagination
                    count={Math.max(1, Math.ceil(batchTotal / 5))}
                    page={batchPage}
                    onChange={(_, value) => setBatchPage(value)}
                  />
                </Box>
              )}
            </Stack>
          )}
      </Box>
    </PageArea>
  );
};

type RecentUploadBatch = UploadBatchStatusResponse & {
  createdAt?: string | Date;
  completedAt?: string | Date;
  clientReference?: string;
  groupedProjectInternalReference?: string;
  groupedProjectCargoDescription?: string;
};

type RecentUploadBatchesResult = {
  items?: RecentUploadBatch[];
  totalCount?: number;
};

const UploadBatchPanel: React.FC<{ batch: RecentUploadBatch; projects: ProjectListItem[] }> = ({ batch, projects }) => {
  const { t } = useTranslation();
  const isActive = batch.status !== 'Completed';
  const [expanded, setExpanded] = useState(false);
  const createdAt = batch.createdAt ? new Date(batch.createdAt) : null;
  const completedAt = batch.completedAt ? new Date(batch.completedAt) : null;
  const files = batch.files ?? [];
  const verifiedCount = files.filter((file) => file.documentStatus === 'Verified').length;
  const reviewCount = files.filter((file) => file.documentStatus === 'NeedsReview').length;
  const issueCount = files.filter((file) => ['Rejected', 'Unreadable', 'DeadLettered', 'Failed'].includes(file.documentStatus ?? '')
    || ['DeadLettered', 'Failed'].includes(file.jobStatus ?? '')).length;
  const resolvedProject = projects.find((project) => project.id === batch.groupedProjectId);
  const resolvedProjectReference = batch.groupedProjectInternalReference ?? resolvedProject?.internalReference;
  const resolvedProjectCargo = batch.groupedProjectCargoDescription ?? resolvedProject?.cargoDescription;
  const title = batch.clientReference?.trim() || t('upload.batchTitle', { count: batch.files?.length ?? 0 });

  return (
    <Accordion
      expanded={isActive || expanded}
      onChange={(_, value) => setExpanded(value)}
      disableGutters
      elevation={0}
      TransitionProps={{ timeout: 0, unmountOnExit: true }}
      sx={{ border: 1, borderColor: 'divider', borderRadius: '8px !important', '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 7, py: 4, '& .MuiAccordionSummary-content': { minWidth: 0 } }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(220px, 0.8fr) auto' }, alignItems: 'center', gap: { xs: 4, md: 8 }, width: '100%', minWidth: 0, pr: 4 }}>
          <Stack direction="row" spacing={5} alignItems="center" sx={{ minWidth: 0 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: isActive ? 'primary.50' : 'success.50', color: isActive ? 'primary.main' : 'success.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Inventory2OutlinedIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, overflowWrap: 'anywhere' }}>{title}</Typography>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {batch.clientReference?.trim() && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('upload.batchTitle', { count: files.length })}</Typography>}
                {verifiedCount > 0 && <Typography sx={{ fontSize: 12, color: 'success.main' }}>{t('upload.verifiedCount', { count: verifiedCount })}</Typography>}
                {reviewCount > 0 && <Typography sx={{ fontSize: 12, color: 'warning.main' }}>{t('upload.reviewCount', { count: reviewCount })}</Typography>}
                {issueCount > 0 && <Typography sx={{ fontSize: 12, color: 'error.main' }}>{t('upload.issueCount', { count: issueCount })}</Typography>}
              </Stack>
              {(completedAt || createdAt) && (
                <Typography sx={{ mt: 1, fontSize: 12, color: 'text.secondary' }}>
                  {t(completedAt ? 'upload.completedAt' : 'upload.uploadedAt', { date: (completedAt ?? createdAt)?.toLocaleString() })}
                </Typography>
              )}
            </Box>
          </Stack>

          {!isActive && (
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('upload.batchResult')}</Typography>
              {resolvedProjectReference ? (
                <>
                  <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 600, color: 'primary.main', overflowWrap: 'anywhere' }}>{resolvedProjectReference}</Typography>
                  {resolvedProjectCargo && <Typography noWrap title={resolvedProjectCargo} sx={{ mt: 1, fontSize: 12, color: 'text.secondary' }}>{resolvedProjectCargo}</Typography>}
                </>
              ) : (
                <Typography sx={{ mt: 1, fontSize: 13, fontWeight: 600, color: 'warning.main' }}>{t(files.some(file => file.projectId) ? 'upload.multipleProjects' : 'upload.notFiled')}</Typography>
              )}
            </Box>
          )}

          <Chip size="small" variant="outlined" color={isActive ? 'primary' : issueCount > 0 ? 'warning' : 'success'} label={t(isActive ? 'upload.processing' : 'upload.batchComplete')} sx={{ justifySelf: { xs: 'start', md: 'end' }, flexShrink: 0, fontWeight: 600 }} />
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 7, pb: 7, pt: 0 }}>
        <Stack spacing={5}>
          {batch.status === 'Grouping' && <Alert severity="info">{t('upload.grouping')}</Alert>}
          {batch.groupingRule && batch.groupingRule !== 'PerArchiveMatching' && (
            <Alert severity={['InsufficientSignalsDraft', 'ConflictingSignalsDraft', 'FallbackExactNeedsReview', 'PhotosRequireProject', 'TargetUnavailable'].includes(batch.groupingRule) ? 'warning' : 'info'}
              action={batch.groupedProjectId ? (
                <Button component={RouterLink} to={`/projects/${batch.groupedProjectId}`} size="md">{t('upload.openProject')}</Button>
              ) : <Button component={RouterLink} to="/unfiled" size="md">{t('upload.fileDocuments')}</Button>}>
              {t(`groupingRules.${batch.groupingRule}`)}
            </Alert>
          )}
          <PagedItems items={batch.files ?? []}>{files => files.map((file) => (
            <PipelineFileCard key={file.documentId} file={file} groupedProjectId={batch.groupingRule ? batch.groupedProjectId : undefined} />
          ))}</PagedItems>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const HowStep: React.FC<{ title: string; text: string }> = ({ title, text }) => (
  <Box sx={{ mb: 6 }}>
    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{title}</Typography>
    <Typography variant="textSM" color="text.secondary">
      {text}
    </Typography>
  </Box>
);

const PipelineFileCard: React.FC<{ file: UploadBatchFileStatus; groupedProjectId?: string | null }> = ({ file, groupedProjectId }) => {
  const { t } = useTranslation();
  const { complete } = pipelineState(file.jobStatus, file.documentStatus);
  const canReview = complete && (file.fieldCount ?? 0) > 0;

  return (
    <Paper variant="outlined" sx={{ p: 7, borderRadius: 2 }}>
      <Stack direction="row" spacing={5} alignItems="flex-start">
        <Box sx={{ color: 'text.secondary', bgcolor: 'action.hover', width: 36, height: 40, flexShrink: 0, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <InsertDriveFileOutlinedIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack spacing={1}>
            <Typography title={file.fileName} sx={{ fontWeight: 600, fontSize: 14, overflowWrap: 'anywhere' }}>
              {file.fileName}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            {file.documentTypeCode && <Typography sx={{ fontSize: 12, color: 'text.secondary', overflowWrap: 'anywhere' }}>{file.documentTypeCode.replace(/_/g, ' ').toLowerCase().replace(/^./, (letter) => letter.toUpperCase())}</Typography>}
            {complete && (file.fieldCount ?? 0) > 0 && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {t('upload.fieldCount', { count: file.fieldCount })}
              </Typography>
            )}
          </Stack>
          <Stack direction={{ xs: 'column', sm: complete ? 'row' : 'column' }} alignItems={{ xs: 'stretch', sm: complete ? 'center' : 'stretch' }} justifyContent="space-between" gap={6} sx={{ mt: 6 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}><PipelineStepper currentStage={file.currentStage} jobStatus={file.jobStatus} documentStatus={file.documentStatus} /></Box>
            <Stack direction="row" spacing={5} sx={{ flexShrink: 0 }}>
              {file.projectId && file.projectId !== groupedProjectId && (
                <Link component={RouterLink} to={`/projects/${file.projectId}`} underline="hover" sx={{ fontSize: 13, alignSelf: 'center' }}>
                  {t('upload.openProject')}
                </Link>
              )}
              {canReview && file.documentId && (
                <Button variant="secondary" component={RouterLink} to={`/review/${file.documentId}`} size="md">
                  {t(file.documentStatus === 'NeedsReview' ? 'upload.review' : 'pipeline.viewDocument')}
                </Button>
              )}
            </Stack>
          </Stack>
          {file.lastError && (
            <Typography variant="textSM" color="error" sx={{ mt: 4, display: 'block', overflowWrap: 'anywhere' }}>
              {file.lastError}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};

export default UploadPage;
